import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Download, Monitor, Smartphone, Globe2 } from 'lucide-react';
import { MarketingHeroPill } from '@/components/marketing/v2/MarketingHeroPill';
import {
  DESKTOP_VERSION,
  OS_LABEL,
  type DesktopPackage,
  type DetectedOs,
  type MacArch,
  desktopPackageUrl,
  detectMacArch,
  detectOs,
  macPackage,
  macPackages,
  primaryPackageFor,
} from './shared';

type DeviceCard = {
  Icon: typeof Monitor;
  name: string;
  role: string;
  version: string;
};

const DEVICES: ReadonlyArray<DeviceCard> = [
  { Icon: Monitor, name: 'Desktop', role: 'Caisse comptoir', version: `v${DESKTOP_VERSION}` },
  { Icon: Smartphone, name: 'Mobile', role: 'Terrain · livreur', version: `v${DESKTOP_VERSION}` },
  { Icon: Globe2, name: 'Web', role: 'Gérant · bureau', version: 'à jour' },
];

export default function DownloadHeroV2() {
  const [os, setOs] = useState<DetectedOs>('other');
  const [pkg, setPkg] = useState<DesktopPackage | null>(null);
  const [macArch, setMacArch] = useState<MacArch>('unknown');

  useEffect(() => {
    const detected = detectOs();
    setOs(detected);
    setPkg(primaryPackageFor(detected));

    if (detected === 'mac') {
      detectMacArch().then((arch) => {
        setMacArch(arch);
        if (arch !== 'unknown') {
          setPkg(macPackage(arch));
        }
      });
    }
  }, []);

  const showMacDualCta = os === 'mac' && macArch === 'unknown';
  const mac = showMacDualCta ? macPackages() : null;

  return (
    <section className="bg-paper pb-14 pt-16 md:pb-20 md:pt-20">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div className="grid items-center gap-12 md:grid-cols-[1.1fr_1fr] md:gap-14">
          {/* LEFT — Headline + OS detect */}
          <div>
            <MarketingHeroPill>
              Version {DESKTOP_VERSION} · Web · Desktop · Android
            </MarketingHeroPill>

            <h1 className="mt-7 font-marketing-display text-[clamp(48px,7vw,108px)] font-bold leading-[0.9] tracking-[-0.045em] text-charcoal-800">
              Ta caisse.
              <br />
              <span className="underline-lime">Partout.</span>
              <br />
              <span className="text-charcoal-400">Sur 3 appareils.</span>
            </h1>

            <p className="mt-7 max-w-xl font-marketing-body text-lg leading-[1.55] text-charcoal-600 md:text-xl">
              Ouvre la caisse au comptoir, prends la commande sur le téléphone, consulte le tableau
              de bord au bureau. Même compte, même stock, même ticket.
            </p>

            {/* OS detect banner */}
            <div className="mt-9 flex flex-col items-stretch justify-between gap-4 border-[3px] border-charcoal-800 bg-lime-500 p-5 shadow-neobrutal-6 sm:flex-row sm:items-center">
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center border-2 border-charcoal-800 bg-charcoal-800 text-lime-500">
                  <Monitor className="h-[22px] w-[22px]" strokeWidth={2} />
                </span>
                <div className="leading-tight">
                  <div className="font-marketing-mono text-[10px] font-medium uppercase tracking-[0.1em] text-charcoal-800/70">
                    Système détecté
                  </div>
                  <div className="mt-1 font-marketing-display text-[17px] font-bold tracking-[-0.01em] text-charcoal-800">
                    {OS_LABEL[os]}
                    {pkg && (
                      <span className="ml-2 font-marketing-mono text-[12px] font-medium text-charcoal-800/70">
                        {pkg.format}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {showMacDualCta && mac ? (
                <div className="flex flex-col gap-2 sm:flex-row">
                  <a
                    href={desktopPackageUrl(mac.arm64)}
                    className="inline-flex items-center justify-center gap-2 border-2 border-charcoal-800 bg-charcoal-800 px-4 py-3 font-marketing-display text-[13px] font-bold text-lime-500 shadow-[3px_3px_0_#ffffff] transition-transform duration-150 hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-[5px_5px_0_#ffffff]"
                  >
                    <Download className="h-4 w-4" strokeWidth={2.5} />
                    Apple Silicon
                  </a>
                  <a
                    href={desktopPackageUrl(mac.intel)}
                    className="inline-flex items-center justify-center gap-2 border-2 border-charcoal-800 bg-white px-4 py-3 font-marketing-display text-[13px] font-bold text-charcoal-800 shadow-[3px_3px_0_#111111] transition-transform duration-150 hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-[5px_5px_0_#111111]"
                  >
                    <Download className="h-4 w-4" strokeWidth={2.5} />
                    Intel
                  </a>
                </div>
              ) : pkg ? (
                <a
                  href={desktopPackageUrl(pkg)}
                  className="inline-flex items-center justify-center gap-2 border-2 border-charcoal-800 bg-charcoal-800 px-5 py-3 font-marketing-display text-[14px] font-bold text-lime-500 shadow-[3px_3px_0_#ffffff] transition-transform duration-150 hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-[5px_5px_0_#ffffff]"
                >
                  <Download className="h-4 w-4" strokeWidth={2.5} />
                  Télécharger {pkg.format}
                </a>
              ) : (
                <span className="inline-flex items-center justify-center gap-2 border-2 border-charcoal-800 bg-charcoal-800 px-5 py-3 font-marketing-display text-[14px] font-bold text-lime-500">
                  Détection…
                </span>
              )}
            </div>

            {/* Alt downloads strip */}
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 font-marketing-mono text-[11.5px] uppercase tracking-[0.08em] text-charcoal-500">
              <span>Ou</span>
              <a
                href="#download-matrix"
                className="text-charcoal-800 underline decoration-dotted underline-offset-[4px] hover:text-lime-600"
              >
                Tous les paquets desktop
              </a>
              <a
                href="#download-platforms"
                className="text-charcoal-800 underline decoration-dotted underline-offset-[4px] hover:text-lime-600"
              >
                Android (.apk)
              </a>
              <Link
                to="/login"
                className="text-charcoal-800 underline decoration-dotted underline-offset-[4px] hover:text-lime-600"
              >
                Ouvrir la version Web
              </Link>
            </div>
          </div>

          {/* RIGHT — 3 devices triptych */}
          <aside className="border-[3px] border-charcoal-800 bg-charcoal-800 p-7 text-white shadow-neobrutal-lime-10 md:p-8">
            <div className="flex items-center justify-between border-b border-charcoal-700 pb-4">
              <span className="font-marketing-mono text-[11px] font-medium uppercase tracking-[0.12em] text-charcoal-200">
                3 appareils · 1 compte
              </span>
              <span className="inline-flex items-center gap-1.5 font-marketing-mono text-[10px] uppercase tracking-[0.1em] text-lime-500">
                <span className="h-1.5 w-1.5 rounded-full bg-lime-500 animate-[pulse_2s_ease-in-out_infinite]" />
                Sync temps réel
              </span>
            </div>

            <ul className="mt-5 grid grid-cols-3 gap-3.5">
              {DEVICES.map(({ Icon, name, role, version }) => (
                <li
                  key={name}
                  className="flex flex-col items-center gap-3 border-[1.5px] border-charcoal-700 p-4 text-center transition-colors duration-150 hover:border-lime-500 hover:bg-charcoal-700"
                >
                  <Icon className="h-8 w-8 text-lime-500" strokeWidth={1.8} />
                  <div>
                    <div className="font-marketing-display text-[14px] font-bold tracking-[-0.01em]">
                      {name}
                    </div>
                    <div className="mt-0.5 font-marketing-mono text-[10px] uppercase tracking-[0.08em] text-charcoal-200">
                      {role}
                    </div>
                  </div>
                  <div className="w-full border-t border-dashed border-charcoal-700 pt-2 font-marketing-mono text-[10px] text-lime-400">
                    {version}
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-5 flex items-center justify-between border-t border-charcoal-700 pt-4 font-marketing-mono text-[11px] uppercase tracking-[0.08em] text-charcoal-200">
              <span>Base chiffrée, sync delta</span>
              <span className="text-lime-500 font-semibold">Offline natif</span>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
